import { Injectable } from '@angular/core';
import {Socket} from "socket.io-client";
import {DefaultEventsMap} from "@socket.io/component-emitter";
import {WebsocketService} from "../websocket.service";
import {Store} from "@ngrx/store";
import {FileUploadService} from "./file-upload.service";
import {FileDownloadService} from "./file-download.service";
import ServerFile from "./dtos/server-file.dto";
import * as AuthSelectors from "../../state/auth/auth.selector";
import {selectAuthIsReady} from "../../state/auth/auth.selector";
import {IHasSocket} from "../i-has-socket";
import * as networkSelectors from "../../state/network/network.selector";
import {DoWhenReadyService, Readyables} from "../do-when-ready.service";

@Injectable({
    providedIn: 'root'
})
export class FilesService implements IHasSocket {
    namespace = "files"
    fileSocket: Socket<DefaultEventsMap, DefaultEventsMap>;

    holdingBlob: string
    socketReady = false

    constructor(
        private websocketService: WebsocketService,
        private store: Store,
        private fileUploadService: FileUploadService,
        private fileDownloadService: FileDownloadService,
        private doWhenReady: DoWhenReadyService
    ) {
        this.websocketService.filesSocket.subscribe((socket) => {
            if (!socket) return
            this.fileSocket = socket
            this.fileSocket.connect()
            this.fileSocket.on("connect", () => {
                this.socketReady = true
            })
            this.fileSocket.on("disconnect", () => {
                this.socketReady = false
            })
        })
    }

    async delayUntilSocket() {
        while(!this.socketReady) {
            await this.sleep(0.5)
        }
    }

    async getServerFileIdForNewFile(file: File): Promise<string> {
        await this.delayUntilSocket()
        return await this.fileUploadService.getServerFileIdForNewFile(file, this.fileSocket);
    }

    async getServerFileIdForNewBlob(
        filename: string,
        exifExtension: string,
        fileSize: number): Promise<string> {
        await this.delayUntilSocket()
        return await this.fileUploadService
            .getServerFileIdForNewBlob(
            filename, exifExtension, fileSize, this.fileSocket
        );
    }

    async uploadFile(serverFileId: string, file: File, resize = true, maxSize = 2000) {
        await this.delayUntilSocket()
        await this.fileUploadService.uploadFile(serverFileId, file, this.fileSocket, resize, maxSize);
    }

    async uploadBlob(
        serverFileId: string,
        filename: string,
        exifExtension: string,
        blob: string,
        resize = true,
        maxSize = 2000
    ) {
        await this.delayUntilSocket()
        await this.fileUploadService.uploadBlob(
            serverFileId,
            filename, exifExtension,
            blob,
            this.fileSocket, resize, maxSize
        );
    }

    async getFile(fileId: string) {
        await this.delayUntilSocket()
        return this.fileDownloadService.getFile(fileId, this.fileSocket);
    }

    async downloadFile(doc, data: any, fileName: any) {
        await this.delayUntilSocket()
        return this.fileDownloadService.downloadFile(doc, data, fileName);
    }

    holdBlob(blob: string) {
        this.holdingBlob = blob;
    }
    getBlob() {
        return this.holdingBlob;
    }

    sleep(seconds: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, seconds * 1000));
    }
}
